/**
 * DiscoveryChat — Mandatory discovery session component
 * Conducts an intelligent multi-turn Q&A (up to 6 questions) before starting any session.
 * Used in BrainstormLab, ResearchLab, and ContentStrategist.
 */

import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Brain, ChevronRight, CheckCircle, RefreshCw, Send, Sparkles, SkipForward, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import { useVoice } from '../hooks/useVoice';

const API = process.env.REACT_APP_BACKEND_URL;

const MAX_Q = 6;

/**
 * @param {string}   context       — The user's original idea/topic
 * @param {string}   sessionType   — "brainstorm" | "research" | "strategy"
 * @param {function} onComplete    — Called with { enrichedContext, summary, history }
 * @param {function} onSkip        — Called to skip discovery and use raw context
 * @param {string}   accentColor   — Accent hex color for styling
 * @param {string}   accentBg      — Accent background hex for styling
 */
export default function DiscoveryChat({
  context,
  sessionType = 'brainstorm',
  onComplete,
  onSkip,
  accentColor = '#a68eff',
  accentBg = 'rgba(166,142,255,0.08)',
}) {
  const [phase, setPhase] = useState('loading'); // loading | asking | complete
  const [history, setHistory] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState('');
  const [enrichedContext, setEnrichedContext] = useState(context);
  const [questionsRemaining, setQuestionsRemaining] = useState(MAX_Q);
  const answerRef = useRef(null);
  const { voiceEnabled, setVoiceEnabled, isListening, isSpeaking, speak, startListening } = useVoice();


  useEffect(() => {
    fetchNext([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase === 'asking') answerRef.current?.focus();
  }, [phase, currentQuestion]);

  const fetchNext = async (hist) => {
    setPhase('loading');
    try {
      const { data } = await axios.post(`${API}/api/discovery/next`, {
        context,
        history: hist,
        session_type: sessionType,
        max_questions: MAX_Q,
      });

      if (data.is_complete) {
        setSummary(data.summary || '');
        setEnrichedContext(data.enriched_context || context);
        setPhase('complete');
        speak('Discovery complete. I have everything I need to begin.');
      } else {
        setCurrentQuestion(data.question || '');
        setQuestionsRemaining(data.questions_remaining ?? MAX_Q - hist.length - 1);
        setPhase('asking');
        speak(data.question || '');
      }
    } catch (e) {
      // Fallback: just show a basic question
      setCurrentQuestion("What does success look like for this session in 90 days?");
      setQuestionsRemaining(MAX_Q - hist.length - 1);
      setPhase('asking');
    }
  };

  const submitAnswer = async () => {
    if (!answer.trim() || submitting) return;
    setSubmitting(true);

    const newHistory = [...history, { question: currentQuestion, answer: answer.trim() }];
    setHistory(newHistory);
    setAnswer('');
    setSubmitting(false);
    await fetchNext(newHistory);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitAnswer();
    }
  };

  const handleComplete = () => {
    onComplete({ enrichedContext, summary, history });
  };

  const handleSkip = () => {
    if (onSkip) onSkip(context);
  };

  const questionsAsked = history.length;
  const progress = Math.round((questionsAsked / MAX_Q) * 100);

  return (
    <div
      className="rounded-2xl overflow-hidden"
      data-testid="discovery-chat"
      style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${accentColor}20` }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ background: accentBg, borderBottom: `1px solid ${accentColor}20` }}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="w-7 h-7 rounded-xl flex items-center justify-center"
            style={{ background: `${accentColor}20` }}
          >
            <Brain size={14} style={{ color: accentColor }} />
          </div>
          <div>
            <div className="text-xs font-bold text-white">
              Smart Discovery
              <span
                className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                style={{ background: `${accentColor}15`, color: accentColor }}
              >
                {questionsAsked}/{MAX_Q} questions
              </span>
            </div>
            <div className="text-[9px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Asking smart questions to build a better session
            </div>
          </div>
        </div>

        {/* Progress + Skip */}
        <div className="flex items-center gap-2">
          <div className="w-20 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${accentColor}, ${accentColor}aa)` }}
            />
          </div>
          {questionsAsked > 0 && onSkip && (
            <button
              onClick={handleSkip}
              data-testid="discovery-skip-btn"
              className="flex items-center gap-1 text-[9px] hover:opacity-70 transition-opacity"
              style={{ color: 'rgba(255,255,255,0.35)' }}
            >
              <SkipForward size={10} /> Skip
            </button>
          )}
        </div>
      </div>

      {/* Conversation history */}
      <div className="px-4 py-3 space-y-3 max-h-72 overflow-y-auto">
        {history.map((item, i) => (
          <div key={i} className="space-y-1.5">
            {/* Question bubble */}
            <div className="flex items-start gap-2.5">
              <div
                className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-black mt-0.5"
                style={{ background: `${accentColor}20`, color: accentColor }}
              >
                {i + 1}
              </div>
              <div
                className="flex-1 text-xs leading-relaxed rounded-xl px-3 py-2"
                style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.7)' }}
              >
                {item.question}
              </div>
            </div>
            {/* Answer bubble */}
            <div className="flex items-start gap-2.5 pl-7">
              <div
                className="flex-1 text-xs leading-relaxed rounded-xl px-3 py-2 font-medium"
                style={{ background: `${accentColor}10`, color: 'rgba(255,255,255,0.85)', border: `1px solid ${accentColor}20` }}
              >
                {item.answer}
              </div>
            </div>
          </div>
        ))}

        {/* Loading */}
        {phase === 'loading' && (
          <div className="flex items-center gap-2 py-2">
            <div className="flex gap-1">
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ background: accentColor, animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
            <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>Thinking...</span>
          </div>
        )}

        {/* Current question */}
        {phase === 'asking' && currentQuestion && (
          <div className="space-y-2">
            <div className="flex items-start gap-2.5">
              <div
                className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-black mt-0.5"
                style={{ background: `${accentColor}20`, color: accentColor }}
              >
                {questionsAsked + 1}
              </div>
              <div
                className="flex-1 text-xs leading-relaxed rounded-xl px-3 py-2.5"
                style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.7)', border: `1px solid ${accentColor}15` }}
              >
                {currentQuestion}
              </div>
            </div>

            {/* Answer input */}
            <div className="flex gap-2 pl-7">
              <textarea
                ref={answerRef}
                value={answer}
                onChange={e => setAnswer(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={2}
                data-testid="discovery-answer-input"
                placeholder="Type your answer... (Enter to submit)"
                className="flex-1 text-xs resize-none outline-none rounded-xl px-3 py-2.5"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${accentColor}25`,
                  color: 'var(--llmc-text)',
                  minHeight: 52,
                }}
              />
              {/* Voice controls */}
              <button onClick={() => startListening(t => setAnswer(t))}
                data-testid="discovery-mic-btn"
                title={isListening ? 'Listening...' : 'Speak your answer'}
                className="self-end flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all"
                style={{ background: isListening ? 'rgba(255,80,80,0.15)' : 'rgba(255,255,255,0.05)', border: `1px solid ${isListening ? 'rgba(255,80,80,0.35)' : 'rgba(255,255,255,0.08)'}`, color: isListening ? '#ff5050' : 'rgba(255,255,255,0.35)' }}>
                {isListening ? <MicOff size={12} className="animate-pulse" /> : <Mic size={12} />}
              </button>
              <button onClick={() => setVoiceEnabled(v => !v)}
                data-testid="discovery-tts-btn"
                title={voiceEnabled ? 'Disable voice questions' : 'Enable Einstein to speak questions aloud'}
                className="self-end flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all"
                style={{ background: voiceEnabled ? 'rgba(61,255,160,0.1)' : 'rgba(255,255,255,0.05)', border: `1px solid ${voiceEnabled ? 'rgba(61,255,160,0.25)' : 'rgba(255,255,255,0.08)'}`, color: voiceEnabled ? '#3dffa0' : 'rgba(255,255,255,0.35)' }}>
                {isSpeaking ? <Volume2 size={12} className="animate-pulse" /> : voiceEnabled ? <Volume2 size={12} /> : <VolumeX size={12} />}
              </button>
              <button
                onClick={submitAnswer}
                disabled={!answer.trim() || submitting}
                data-testid="discovery-submit-btn"
                className="self-end flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:opacity-80 disabled:opacity-30"
                style={{ background: `${accentColor}20`, border: `1px solid ${accentColor}40`, color: accentColor }}
              >
                {submitting ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
              </button>
            </div>

            {questionsRemaining > 0 && (
              <div className="pl-7 text-[9px]" style={{ color: 'rgba(255,255,255,0.25)' }}>
                Up to {questionsRemaining} more question{questionsRemaining !== 1 ? 's' : ''} possible
              </div>
            )}
          </div>
        )}

        {/* Complete state */}
        {phase === 'complete' && (
          <div className="space-y-3">
            <div
              className="rounded-xl p-3 space-y-1.5"
              style={{ background: 'rgba(61,255,160,0.06)', border: '1px solid rgba(61,255,160,0.2)' }}
            >
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: '#3dffa0' }}>
                <CheckCircle size={11} />
                Discovery Complete
              </div>
              {summary && (
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  {summary}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Complete CTA */}
      {phase === 'complete' && (
        <div className="px-4 pb-4">
          <button
            onClick={handleComplete}
            data-testid="discovery-launch-btn"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-black text-white transition-all hover:opacity-90"
            style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}aa)` }}
          >
            <Sparkles size={15} />
            Launch Session with Context
            <ChevronRight size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
